import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Badge, Alert, Button, Spinner, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Map from './Map';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { safeFormatDate } from '../shared/utils';
import { getActivityStyle } from '../shared/activityUtils';

// Add custom CSS to ensure map displays correctly
const mapContainerStyle = {
  height: '600px',
  maxHeight: '70vh',
  width: '100%',
  marginBottom: '20px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  overflow: 'hidden'
};

// Generate a unique key for map locations based on name and GPS coordinates
const generateKey = (name, latitude, longitude) => {
  const nameWithoutSpaces = (name || '').replace(/\s+/g, '');
  const lat = Number(latitude).toFixed(1);
  const lng = Number(longitude).toFixed(1);
  return `${nameWithoutSpaces}-${lat}-${lng}`;
};

export default function LocationsTab({ parsedData, planId, isActive = false, isTemplate = false }) {
  const { t } = useTranslation();
  
  // State management
  const [locations, setLocations] = useState([]);
  const [mapLocations, setMapLocations] = useState([]);
  const [filteredMapLocations, setFilteredMapLocations] = useState([]);
  const [mapKey, setMapKey] = useState(Date.now());
  const isMapVisible = true;
  const isLoading = false;
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [filters, setFilters] = useState({
    attraction: true,
    accommodation: true,
    transportation: true
  });
  
  // Helper to translate activity type
  const translateActivityType = (type) => {
    if (type === 'transportation') {
      return t('planDetail.locations.transportation');
    }
    if (type === 'accommodation') {
      return t('planDetail.mainLocations.accommodation');
    }
    if (type === 'attraction') {
      // Use singular form for individual activities, but the filter uses plural
      return t('planDetail.locations.attractions');
    }
    return type; // Fallback to original type
  };

  // Simpler useEffect to load map
  useEffect(() => {
    // Only regenerate map key when tab becomes active
    if (isActive) {
      setMapKey(Date.now());
    }
  }, [isActive]);

  // Apply filters to map locations
  useEffect(() => {
    const filtered = mapLocations.filter(location => {
      const locationType = location.type?.toLowerCase();
      if (locationType === 'attraction') return filters.attraction;
      if (locationType === 'accommodation') return filters.accommodation;
      if (locationType === 'transportation') return filters.transportation;
      return true; // Show other types by default
    });
    
    setFilteredMapLocations(filtered);
  }, [mapLocations, filters]);

  // Extract activities from plan data - SIMPLE
  useEffect(() => {
    if (parsedData) {
      const extractedActivities = [];
      const mapData = [];
      
      // Extract all activities from all days
      if (parsedData.days && parsedData.days.length > 0) {
        parsedData.days.forEach((day, dayIndex) => {
          if (day.activities && day.activities.length > 0) {
            day.activities.forEach(activity => {
              // Get address
              let address = '';
              if (activity.location) {
                address = typeof activity.location === 'object' ? activity.location.address : activity.location;
              }
              
              // For transportation, show from and to
              if (activity.type === 'transportation') {
                let fromAddress = '';
                let toAddress = '';
                
                if (activity.fromLocation) {
                  fromAddress = typeof activity.fromLocation === 'object' ? activity.fromLocation.address : activity.fromLocation;
                }
                if (activity.toLocation) {
                  toAddress = typeof activity.toLocation === 'object' ? activity.toLocation.address : activity.toLocation;
                }
                
                if (fromAddress && toAddress) {
                  address = `${fromAddress} → ${toAddress}`;
                } else if (fromAddress) {
                  address = `${t('planDetail.locations.from')}: ${fromAddress}`;
                } else if (toAddress) {
                  address = `${t('planDetail.locations.to')}: ${toAddress}`;
                }
              }
              
              // Only add if has address
              if (address) {
                extractedActivities.push({
                  name: activity.name,
                  type: activity.type || 'activity',
                  day: dayIndex + 1,
                  date: isTemplate ? '' : (day.date ? safeFormatDate(day.date) : ''),
                  address: address
                });
                
                // Add to map data if coordinates exist
                if (activity.location && typeof activity.location === 'object' && activity.location.latitude && activity.location.longitude) {
                  mapData.push({
                    key: generateKey(activity.name, activity.location.latitude, activity.location.longitude),
                    name: activity.name,
                    type: activity.type || 'activity',
                    day: dayIndex + 1,
                    date: isTemplate ? '' : (day.date ? safeFormatDate(day.date) : ''),
                    dateLabel: isTemplate ? `${t('planDetail.locations.day')} ${dayIndex + 1}` : `${dayIndex + 1} (${day.date ? safeFormatDate(day.date) : ''})`,
                    address: address,
                    gps: {
                      latitude: Number(activity.location.latitude),
                      longitude: Number(activity.location.longitude)
                    }
                  });
                }
                
                // For transportation activities, add both fromLocation and toLocation if they have coordinates
                if (activity.type === 'transportation') {
                  if (activity.fromLocation && typeof activity.fromLocation === 'object' && activity.fromLocation.latitude && activity.fromLocation.longitude) {
                    const fromAddress = typeof activity.fromLocation === 'object' ? activity.fromLocation.address : activity.fromLocation;
                    mapData.push({
                      key: generateKey(activity.name, activity.fromLocation.latitude, activity.fromLocation.longitude),
                      name: `${activity.name} (${t('planDetail.locations.from')})`,
                      type: activity.type || 'activity',
                      day: dayIndex + 1,
                      date: isTemplate ? '' : (day.date ? safeFormatDate(day.date) : ''),
                      dateLabel: isTemplate ? `${t('planDetail.locations.day')} ${dayIndex + 1}` : `${dayIndex + 1} (${day.date ? safeFormatDate(day.date) : ''})`,
                      address: fromAddress,
                      gps: {
                        latitude: Number(activity.fromLocation.latitude),
                        longitude: Number(activity.fromLocation.longitude)
                      }
                    });
                  }
                  
                  if (activity.toLocation && typeof activity.toLocation === 'object' && activity.toLocation.latitude && activity.toLocation.longitude) {
                    const toAddress = typeof activity.toLocation === 'object' ? activity.toLocation.address : activity.toLocation;
                    mapData.push({
                      key: generateKey(activity.name, activity.toLocation.latitude, activity.toLocation.longitude),
                      name: `${activity.name} (${t('planDetail.locations.to')})`,
                      type: activity.type || 'activity',
                      day: dayIndex + 1,
                      date: isTemplate ? '' : (day.date ? safeFormatDate(day.date) : ''),
                      dateLabel: isTemplate ? `${t('planDetail.locations.day')} ${dayIndex + 1}` : `${dayIndex + 1} (${day.date ? safeFormatDate(day.date) : ''})`,
                      address: toAddress,
                      gps: {
                        latitude: Number(activity.toLocation.latitude),
                        longitude: Number(activity.toLocation.longitude)
                      }
                    });
                  }
                }
              }
            });
          }
        });
      }
      
      setLocations(extractedActivities);
      setMapLocations(mapData);
    }
  }, [parsedData, isTemplate, t]);

  // Handle clicking an activity in the list
  const handleActivityClick = (activity) => {
    setSelectedActivity(selectedActivity?.name === activity.name ? null : activity);
  };

  // Handle clicking a marker on the map
  const handleMarkerClick = (location) => {
    console.log("Marker clicked:", location);
  };

  // Handle filter change
  const handleFilterChange = (type) => {
    setFilters(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <div className="locations-tab-container">
      <h4 className="mb-4">{t('planDetail.locations.tripActivitiesLocations')}</h4>
      
      {locations.length === 0 ? (
        <Alert variant="info">{t('planDetail.locations.noActivitiesFound')}</Alert>
      ) : (
        <div>
          {/* Map header with controls and filters */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2">
            <div>
              <h5 className="m-0 mb-2 mb-md-0">
                {t('planDetail.locations.map')} ({filteredMapLocations.length} {t('planDetail.locations.locations')})
              </h5>
            </div>
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
              <div className="me-0 me-md-3 mb-2 mb-md-0">
                <Form>
                  <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-3">
                    <Form.Check 
                      type="checkbox"
                      id="filter-attraction"
                      label={
                        <span>
                          <span 
                            className="filter-icon me-2"
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#c33',
                              display: 'inline-block',
                              verticalAlign: 'middle'
                            }}
                          ></span>
                          {t('planDetail.locations.attractions')}
                        </span>
                      }
                      checked={filters.attraction}
                      onChange={() => handleFilterChange('attraction')}
                    />
                    <Form.Check 
                      type="checkbox"
                      id="filter-transportation"
                      label={
                        <span>
                          <span 
                            className="filter-icon me-2"
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#2ecc71',
                              display: 'inline-block',
                              verticalAlign: 'middle'
                            }}
                          ></span>
                          {t('planDetail.locations.transportation')}
                        </span>
                      }
                      checked={filters.transportation}
                      onChange={() => handleFilterChange('transportation')}
                    />
                    <Form.Check 
                      type="checkbox"
                      id="filter-accommodation"
                      label={
                        <span>
                          <span 
                            className="filter-icon me-2"
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#3388ff',
                              display: 'inline-block',
                              verticalAlign: 'middle'
                            }}
                          ></span>
                          {t('planDetail.locations.accommodations')}
                        </span>
                      }
                      checked={filters.accommodation}
                      onChange={() => handleFilterChange('accommodation')}
                    />
                  </div>
                </Form>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setMapKey(Date.now())}
                title={t('planDetail.locations.reloadMapTooltip')}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    {t('planDetail.locations.reloadMap')}
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Map container */}
          <div style={mapContainerStyle}>
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <div className="text-center">
                  <Spinner animation="border" className="mb-3" />
                  <p>{t('planDetail.locations.loadingMap')}</p>
                </div>
              </div>
            ) : isMapVisible && isActive && (
              <Map 
                key={mapKey}
                locations={filteredMapLocations} 
                height="100%" 
                className="shadow-sm"
                onMarkerClick={handleMarkerClick}
                planId={planId}
                selectedActivity={selectedActivity}
              />
            )}
            {!isActive && (
              <div className="d-flex justify-content-center align-items-center h-100 bg-light">
                <div className="text-center text-muted">
                  <i className="bi bi-map display-4 mb-3"></i>
                  <p>{t('planDetail.locations.mapWillBeDisplayed')}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Simple Activities List */}
          <div style={{ marginTop: 20 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">{t('planDetail.locations.activities')} ({locations.length})</h4>
              <small className="text-muted">
                <i className="bi bi-hand-index me-1"></i>
                {t('planDetail.locations.clickToHighlight')}
              </small>
            </div>
            <Card>
              <ListGroup variant="flush">
                {locations.map((activity, index) => {
                  const style = getActivityStyle(activity.type);
                  const isSelected = selectedActivity?.name === activity.name;
                  return (
                    <ListGroup.Item 
                      key={index} 
                      className={`py-3 ${isSelected ? 'border-primary border-2 bg-light' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleActivityClick(activity)}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1 fw-bold">
                            {activity.name}
                            {isSelected && <i className="bi bi-geo-alt-fill ms-2 text-primary"></i>}
                          </h6>
                          <Badge bg={style.bg} className="me-2">{translateActivityType(activity.type)}</Badge>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold text-primary">{t('planDetail.locations.day')} {activity.day}</div>
                          {activity.date ? <small className="text-muted">{activity.date}</small> : null}
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <i className="bi bi-geo-alt me-1 text-muted"></i>
                        <span className="text-muted">{activity.address}</span>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
} 